import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { mediaUrl, assetUrl } from "@/lib/api";

const VALUES = [
  { t: "Bespoke by Design", d: "No two celebrations are alike. Every plan begins with you." },
  { t: "Calm Under Pressure", d: "We handle the logistics and the unexpected, so the day feels seamless." },
  { t: "An Eye for Detail", d: "The smallest touches are what guests remember. We obsess over them." },
  { t: "International Reach", d: "From city ballrooms to destination venues, we coordinate celebrations around the world." },
];

export default function About() {
  const { content: c } = useContent();
  const blocks = Array.isArray(c.about_blocks) ? c.about_blocks : [];
  return (
    <div data-testid="about-page" className="pt-28 md:pt-32">
      <section className="container-lux py-16 md:py-24">
        <div className="grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <Reveal>
              <p className="overline mb-6">About Us</p>
              <h1 className="font-serif-lux text-5xl md:text-7xl font-light leading-[1.02]">
                {c.about_h1}
              </h1>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <p className="text-[#4A4A4A] text-lg leading-relaxed">{c.about_intro}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="container-lux pb-8">
        <Reveal>
          <div className="media-zoom aspect-[16/9] md:aspect-[21/9]">
            <img src={mediaUrl(c.about_image)} alt="Grand venue" />
          </div>
        </Reveal>
      </section>

      <section className="container-lux py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <Reveal>
              <h2 className="font-serif-lux text-3xl md:text-4xl font-light leading-tight mb-6">
                {c.about_h2}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.1}>
              <p className="text-[#4A4A4A] text-lg leading-relaxed mb-6">{c.about_p1}</p>
              <p className="text-[#4A4A4A] leading-relaxed">{c.about_p2}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {blocks.length > 0 && (
        <section className="container-lux py-8 md:py-16 space-y-20 md:space-y-28" data-testid="about-blocks">
          {blocks.map((b, i) => (
            <Reveal key={i}>
              <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center" data-testid={`about-block-${i}`}>
                <figure className={`editorial-photo w-full aspect-[4/5] ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  {b.image ? (
                    <img src={assetUrl(b.image)} alt={b.headline || ""} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full hero-full-placeholder" />
                  )}
                  <span className="editorial-grade" />
                </figure>
                <div>
                  {b.headline && <h2 className="font-serif-lux text-3xl md:text-4xl font-light text-[#47562F] leading-tight mb-6">{b.headline}</h2>}
                  {b.body && <p className="text-[#4A4A4A] text-lg leading-relaxed whitespace-pre-line">{b.body}</p>}
                </div>
              </div>
            </Reveal>
          ))}
        </section>
      )}

      <section className="bg-[#0A0A0A] text-white py-24 md:py-32">
        <div className="container-lux">
          <Reveal><p className="overline mb-14">What Guides Us</p></Reveal>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-14">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08}>
                <div className="border-t border-white/15 pt-7">
                  <h3 className="font-serif-lux text-2xl md:text-3xl font-light mb-4 text-[#D4AF37]">{v.t}</h3>
                  <p className="text-white/70 leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-lux py-24 md:py-32 text-center">
        <Reveal>
          <h2 className="font-serif-lux text-4xl md:text-5xl font-light mb-8">{c.about_cta_heading}</h2>
          <Link to="/quote" data-testid="about-quote-btn" className="btn-gold">Begin Your Journey</Link>
        </Reveal>
      </section>
    </div>
  );
}
